// The line drawer of a SEA or PEA budget (SPEC §8.6): one cost item of a vessel's budget in the
// period, its figures as the budget inquiry adds them up, and its ledger with a running balance.
import { useQuery } from "@tanstack/react-query";
import { formatDate } from "@po/shared";
import { api, type AutoCountLine } from "../lib/api";
import { ADDED, Equation, Op, Stepper, Term } from "./BudgetLineDrawer";
import { OverChip, signed, usd, UsedBar } from "./budget";
import type { DrawerRef } from "./Drawer";

/** Which line: the vessel's project in its database, the cost item and its project status. */
export type AutoCountLineRef = { source: string; database: string; projectNo: string; costItem: string; statusKey: string };

export const sameAutoCountLine = (a: AutoCountLineRef, b: AutoCountLineRef) =>
  a.source === b.source && a.database === b.database && a.projectNo === b.projectNo && a.costItem === b.costItem && a.statusKey === b.statusKey;

const periodLabel = (p: { year: number; quarter: number | null }) => (p.quarter === null ? `Year ${p.year}` : `Q${p.quarter} ${p.year}`);

const TYPE: Record<AutoCountLine["entries"][number]["kind"], string> = {
  base: "BASE",
  carry_forward: "CARRY FWD",
  fund: "FUND",
  transfer_in: "TRANSFER IN",
  transfer_out: "TRANSFER OUT",
  reserve: "OTHER COST",
  actual: "PO",
};

const left = { textAlign: "left" } as const;
const TAKEN = { color: "var(--budget-over)", fontWeight: 600 } as const;
const MOVES = new Set(["carry_forward", "fund", "transfer_in", "transfer_out"]);

function Ledger({ l, onOpen }: { l: AutoCountLine; onOpen: (r: DrawerRef) => void }) {
  let balance = 0;
  const rows = l.entries.map((e) => ({ e, balance: (balance += e.amountUsd) }));
  return (
    <section className="py-3 border-t border-line">
      <div className="overflow-x-auto">
        <table className="sheet text-[12px]">
          <thead>
            <tr>
              <th>Date</th>
              <th style={left} title="Who entered it; a SEA or PEA user linked to a Sertica user shows as that user">User</th>
              <th style={left}>Type</th>
              <th style={left}>Description</th>
              <th>Amount</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ e, balance }, i) => (
              <tr key={i}>
                <td style={{ whiteSpace: "nowrap" }}>{e.date ? formatDate(e.date) : "—"}</td>
                <td style={left} title={e.userId ? `AutoCount user ${e.userId}` : undefined}>
                  {e.user ?? (e.kind === "base" ? "" : "—")}
                </td>
                <td style={MOVES.has(e.kind) ? { ...left, ...(e.amountUsd < 0 ? TAKEN : ADDED) } : left}>{TYPE[e.kind]}</td>
                <td style={{ ...left, whiteSpace: "normal", minWidth: 160 }}>
                  {e.docNo ? (
                    <>
                      {e.poNo ? (
                        <button className="text-brand font-semibold hover:underline" onClick={() => onOpen({ type: "po", no: e.poNo! })}>
                          {e.docNo}
                        </button>
                      ) : (
                        <span className="font-semibold" title="Not in the store: only POs on the mapped vessels dated, submitted or reserved from 2024 are read">
                          {e.docNo}
                        </span>
                      )}
                      {e.text && <span className="text-muted"> · {e.text}</span>}
                      {e.kind === "reserve" && <span className="text-muted"> · reserved, not transferred yet</span>}
                    </>
                  ) : (
                    (e.text?.trim() ?? "—")
                  )}
                  {l.period.quarter === null && e.kind !== "base" && <span className="text-muted"> · Q{e.quarter}</span>}
                </td>
                <td className={e.amountUsd < 0 ? "neg" : ""} style={MOVES.has(e.kind) && e.amountUsd > 0 ? ADDED : undefined}>
                  {e.kind === "base" ? usd(e.amountUsd) : signed(e.amountUsd)}
                </td>
                <td style={balance < -0.005 ? { background: "var(--budget-over)", color: "#fff", fontWeight: 600 } : undefined}>{usd(balance)}</td>
              </tr>
            ))}
            {rows.length <= 1 && (
              <tr>
                <td colSpan={6} className="text-muted">
                  No carry forward, fund, transfers or POs in this period.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * The line's figures as on Sertica's line drawer, read left to right: the base it started with,
 * plus carry forward, transfers in and fund, less transfers out, is the budget; the budget less
 * actual and other cost (SEA's reserve) is remaining, SEA's balance.
 */
function Summary({ l }: { l: AutoCountLine }) {
  const move = (label: string, n: number, minus = false) => (
    <>
      <Op>{minus ? "−" : "+"}</Op>
      <Term label={label}>
        <span className={minus && n > 0.005 ? "neg" : undefined} style={!minus && n > 0.005 ? ADDED : undefined}>
          {usd(n)}
        </span>
      </Term>
    </>
  );
  return (
    <section className="card mx-4 my-3 p-3 space-y-3">
      <Equation>
        <Term label="Base">{usd(l.budgetUsd)}</Term>
        {move(l.period.quarter === null ? "Carry fwd (from last year)" : "Carry fwd", l.carryForwardUsd)}
        {move("Transfer in", l.transferInUsd)}
        {move("Fund", l.fundUsd)}
        {move("Transfer out", l.transferOutUsd, true)}
        <Op>=</Op>
        <Term label="Budget" result>
          {usd(l.netBudgetUsd)}
        </Term>
      </Equation>
      <Equation>
        <Term label="Budget">{usd(l.netBudgetUsd)}</Term>
        <Op>−</Op>
        <Term label="Actual">{usd(l.actualUsd)}</Term>
        <Op>−</Op>
        <Term label="Other cost">{usd(l.reserveUsd)}</Term>
        <Op>=</Op>
        <Term label="Remaining" result>
          <span className={l.balanceUsd <= -1 ? "neg" : undefined}>{usd(l.balanceUsd)}</span>
        </Term>
      </Equation>
      <UsedBar budgetUsd={l.netBudgetUsd} spendUsd={l.actualUsd} width={220} />
    </section>
  );
}

export function AutoCountLineBody({ line, period, onOpen, steps, onStep }: { line: AutoCountLineRef; period: string; onOpen: (r: DrawerRef) => void; steps?: AutoCountLineRef[]; onStep?: (l: AutoCountLineRef) => void }) {
  const params = { source: line.source, database: line.database, project: line.projectNo, item: line.costItem, status: line.statusKey, period };
  const { data: l, isLoading, isError, isPlaceholderData } = useQuery({
    queryKey: ["budgets/autocount/line", params],
    queryFn: () => api<AutoCountLine>(`/budgets/autocount/line?${new URLSearchParams(params)}`),
    // Stepping keeps the last line on screen, dimmed, until the next one loads.
    placeholderData: (prev) => prev,
  });
  const stepper = steps && onStep && <Stepper steps={steps} at={steps.findIndex((s) => sameAutoCountLine(s, line))} onStep={onStep} />;
  if (isLoading || isError || !l)
    return (
      <>
        {stepper}
        <div className="p-4 text-muted">{isLoading ? "Loading…" : "This line is not in the vessel's budget for this period."}</div>
      </>
    );
  return (
    <div className={isPlaceholderData ? "opacity-60" : undefined}>
      {stepper}
      <div className="px-4 pt-3 pb-2 space-y-2">
        <div>
          <div className="label">
            {l.source} budget line · {periodLabel(l.period)}
          </div>
          <h2 className="text-[18px] font-extrabold leading-snug">
            {l.vesselName} <span className="text-muted font-semibold">· {[l.expenseGroup, l.costItem].filter(Boolean).join(" · ")}</span>
          </h2>
          <div className="text-[12px] text-muted">{[l.statusName, l.database, `project ${l.projectNo}`, l.companyName].filter(Boolean).join(" · ")}</div>
        </div>
        {l.over && (
          <div className="flex flex-wrap items-center gap-1.5">
            <OverChip title={`Over ${l.source}'s budget by $1 or more; not the Sertica flag`} />
            <span className="text-[12.5px] neg tnum">over by {usd(-l.balanceUsd)}</span>
          </div>
        )}
      </div>
      <Summary l={l} />
      <Ledger l={l} onOpen={onOpen} />
      <p className="px-4 pb-4 text-[11px] text-muted">
        USD. POs count by submit date, at the PO date&apos;s USD rate, once transferred; other cost is SEA&apos;s reserve, POs not transferred yet. A quarter&apos;s carry forward is the last quarter&apos;s balance{l.period.quarter === null ? ", so over a year only Q1's is listed" : ""}.
      </p>
    </div>
  );
}
